import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { LuPlus } from "react-icons/lu";
import { TailSpin } from "react-loader-spinner";
import Tabs from "../../components/tab";
import Table from "../../components/table";
import Button from "../../components/button";
import useRequest from "../../components/hooks/use-request";
import StatusPill from "./status-pill";
import { formatDate } from "../../utilities/functions";
import {
  EmailCampaign,
  EmailTemplate,
  audienceSummary,
  prettyStatus,
} from "./api";

const TABS = ["Campaigns", "Templates"];

const CAMPAIGN_STATUSES = [
  "draft",
  "pending_approval",
  "scheduled",
  "sending",
  "sent",
  "cancelled",
];

// List endpoints may wrap the array under a key; accept the common shapes.
const asList = (data: any, key: string): any[] => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.[key])) return data[key];
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

const EmptyState = ({ label }: { label: string }) => (
  <div className="flex justify-center items-center h-56">
    <p className="text-gray-500 text-[15px]">{label}</p>
  </div>
);

const Loader = () => (
  <div className="flex justify-center items-center h-56">
    <TailSpin color="skyblue" />
  </div>
);

// ─── Campaigns tab ──────────────────────────────────────────────────────────
const CampaignsTab = () => {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState<EmailCampaign[]>([]);
  const [status, setStatus] = useState("");
  const { makeRequest, loading } = useRequest("/email-campaigns", "GET");

  const fetchCampaigns = useCallback(async () => {
    const [res] = await makeRequest(undefined, status ? { status } : undefined);
    if (res?.data !== undefined) setCampaigns(asList(res.data, "campaigns"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  const columns = [
    { header: "Name", accessor: "name" },
    {
      header: "Category",
      accessor: "category",
      render: (row: any) => (
        <span className="capitalize">{row.category || "—"}</span>
      ),
    },
    {
      header: "Audience",
      accessor: "audience",
      render: (row: any) => (
        <span className="text-[12px] text-gray-600">
          {audienceSummary(row.audienceFilter)}
        </span>
      ),
    },
    {
      header: "Status",
      accessor: "campaignStatus",
      render: (row: any) => <StatusPill value={row.status} />,
    },
    {
      header: "Scheduled",
      accessor: "scheduled",
      render: (row: any) => (row.scheduledAt ? formatDate(row.scheduledAt) : "—"),
    },
    {
      header: "Last updated",
      accessor: "updatedOn",
      render: (row: any) => (row.updated_at ? formatDate(row.updated_at) : "—"),
    },
    {
      header: "",
      accessor: "openCampaign",
      render: (row: any) => (
        <button
          onClick={() => navigate(`/email-management/campaigns/${row.id}`)}
          className="text-[#0979A1] text-[13px] font-medium hover:underline whitespace-nowrap">
          Open
        </button>
      ),
    },
  ];

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <label className="text-[11px] text-gray-500 block mb-0.5">
            Filter by status
          </label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-[13px] outline-none">
            <option value="">All statuses</option>
            {CAMPAIGN_STATUSES.map((s) => (
              <option key={s} value={s}>
                {prettyStatus(s)}
              </option>
            ))}
          </select>
        </div>
        <Button
          size="sm"
          variant="primary"
          type="button"
          onClick={() => navigate("/email-management/campaigns/new")}>
          <span className="flex items-center gap-1">
            <LuPlus size={14} /> New Campaign
          </span>
        </Button>
      </div>

      {loading ? (
        <Loader />
      ) : campaigns.length ? (
        <div className="overflow-x-auto">
          <Table
            columns={columns}
            data={campaigns}
            sn={false}
            selectedUserId={null}
            onUserClick={(id: string) =>
              id && navigate(`/email-management/campaigns/${id}`)
            }
          />
        </div>
      ) : (
        <EmptyState label="No campaigns yet" />
      )}
    </div>
  );
};

// ─── Templates tab ──────────────────────────────────────────────────────────
const TemplatesTab = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const { makeRequest, loading } = useRequest("/email-templates", "GET");

  useEffect(() => {
    const fetch = async () => {
      const [res] = await makeRequest();
      if (res?.data !== undefined) setTemplates(asList(res.data, "templates"));
    };
    fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = [
    { header: "Name", accessor: "name" },
    {
      header: "Category",
      accessor: "category",
      render: (row: any) => (
        <span className="capitalize">{row.category || "—"}</span>
      ),
    },
    {
      header: "Subject",
      accessor: "subject",
      render: (row: any) => (
        <span className="text-[12px] text-gray-600">{row.subject || "—"}</span>
      ),
    },
    {
      header: "Status",
      accessor: "templateStatus",
      render: (row: any) => <StatusPill value={row.status} />,
    },
    {
      header: "Last updated",
      accessor: "updatedOn",
      render: (row: any) => (row.updated_at ? formatDate(row.updated_at) : "—"),
    },
    {
      header: "",
      accessor: "openTemplate",
      render: (row: any) => (
        <button
          onClick={() => navigate(`/email-management/templates/${row.id}`)}
          className="text-[#0979A1] text-[13px] font-medium hover:underline whitespace-nowrap">
          Edit
        </button>
      ),
    },
  ];

  return (
    <div>
      {loading ? (
        <Loader />
      ) : templates.length ? (
        <div className="overflow-x-auto">
          <Table
            columns={columns}
            data={templates}
            sn={false}
            selectedUserId={null}
            onUserClick={(id: string) =>
              id && navigate(`/email-management/templates/${id}`)
            }
          />
        </div>
      ) : (
        <EmptyState label="No templates yet" />
      )}
    </div>
  );
};

// ─── Shell ──────────────────────────────────────────────────────────────────
const EmailManagement = () => {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(
    (location.state as any)?.tab || "Campaigns"
  );

  return (
    <>
      <div className="w-[95%]">
        <h2 className="text-[24px] font-bold">
          Email Management
          <p className="text-[14px] font-normal">
            Create, approve and send newsletters to a filtered audience
          </p>
        </h2>
      </div>

      <div className="mt-6 w-[95%]">
        <Tabs
          activeTab={activeTab}
          tabs={TABS}
          setActiveTab={setActiveTab}
          variant="underline"
          className="mb-6"
        />
        {activeTab === "Campaigns" && <CampaignsTab />}
        {activeTab === "Templates" && <TemplatesTab />}
      </div>
    </>
  );
};

export default EmailManagement;
